import { useMemo, useState } from 'react';
import { History, Loader2, PencilLine } from 'lucide-react';
import { cn, HistoryDialog } from '@my/ui';
import { useAuditTrail, useInvoice } from '@my/tms-api/hooks';
import type { Invoice, InvoiceLine } from '@my/tms-api';
import { useOrganisationVatTreatments } from '@my/directory-api/hooks';
import { vatTreatmentLineLabel } from '@my/directory-api';
import { buildVatBands, type DocumentLine } from '@/features/settings/documents/lib/documentData';

/**
 * Everything an invoice says, line by line, without opening the PDF — what it
 * was raised with (VAT schema and the treatment line that chose it, nominal,
 * category, the goods carried) and what an operator changed by hand.
 *
 * <p>
 * Read from the invoice's own snapshot (`GET invoices/{id}`), never the live
 * charge or customer, so it shows what the document says even after the
 * customer, tax schema or charge has been edited since.
 * </p>
 */
export function InvoiceDetails({
  invoiceId,
  organisationId,
}: {
  invoiceId: string;
  organisationId: string | null;
}) {
  const query = useInvoice(invoiceId);
  // Only for naming the treatment line the way Settings does ("IE-3").
  const matrixQuery = useOrganisationVatTreatments(organisationId);

  if (query.isPending) {
    return (
      <Shell>
        <p className="py-4 text-center text-xs text-slate-400">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />
          Loading invoice…
        </p>
      </Shell>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Shell>
        <p className="py-4 text-center text-xs text-red-600">Could not load this invoice.</p>
      </Shell>
    );
  }
  return (
    <Details
      invoice={query.data}
      organisationId={organisationId}
      lineLabel={(key) => vatTreatmentLineLabel(matrixQuery.data, key)}
    />
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="border-y-2 border-sky-200 bg-sky-50/40 p-3">{children}</div>;
}

type Override = { field: string; from: string | null; to: string | null };

/** `{"Vat":{"from":…,"to":…}}` → a list; tolerant of a malformed value. */
function parseOverrides(json: string | null | undefined): Override[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as Record<string, { from?: string | null; to?: string | null }>;
    return Object.entries(parsed).map(([field, v]) => ({
      field,
      from: v?.from ?? null,
      to: v?.to ?? null,
    }));
  } catch {
    return [];
  }
}

const OVERRIDE_NAME: Record<string, string> = { Vat: 'VAT', Nominal: 'Nominal', Category: 'Category' };

function Details({
  invoice,
  organisationId,
  lineLabel,
}: {
  invoice: Invoice;
  organisationId: string | null;
  lineLabel: (key: string) => string;
}) {
  const iso = invoice.currencyIsoCode;
  const lines = useMemo(
    () => invoice.lines.slice().sort((a, b) => a.sequence - b.sequence),
    [invoice.lines],
  );
  const showCategory = lines.some((l) => l.accountingCategoryCode);
  const overridden = lines.filter((l) => parseOverrides(l.overridesJson).length > 0);

  // The same banding the PDF uses — one per tax schema, worded.
  const bands = useMemo(
    () =>
      buildVatBands(
        lines.map(
          (l) =>
            ({
              id: l.id,
              description: l.description,
              net: l.net,
              taxRatePct: l.taxRatePct,
              taxAmount: l.taxAmount,
              gross: l.net + l.taxAmount,
              taxRateId: l.taxRateId ?? null,
              taxRateName: l.taxRateName ?? null,
              taxType: l.taxType ?? null,
              taxDescription: l.taxDescription ?? null,
            }) as DocumentLine,
        ),
      ),
    [lines],
  );
  const reverseCharge = lines.some((l) => l.taxType === 'ReverseCharge');

  return (
    <Shell>
      {/* ── What the invoice was raised for ─────────────────────────────── */}
      <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-2 rounded-md border border-slate-200 bg-white p-3 text-xs md:grid-cols-4">
        <Fact label="Customer">
          <span className="font-semibold text-slate-800">{invoice.billingName || invoice.customerName}</span>
          {invoice.customerCode && <span className="ml-1 text-slate-400">{invoice.customerCode}</span>}
        </Fact>
        <Fact label="VAT number">
          {invoice.customerTaxNumber ? (
            <>
              {invoice.customerTaxNumber}
              {invoice.customerTaxCountry && (
                <span className="ml-1 text-slate-400">({invoice.customerTaxCountry})</span>
              )}
            </>
          ) : (
            <span className={reverseCharge ? 'font-medium text-amber-700' : 'text-slate-400'}>
              {reverseCharge ? 'None — required on a reverse-charge invoice' : '—'}
            </span>
          )}
        </Fact>
        <Fact label="Issued / due">
          {fmtDate(invoice.issueDate)} <span className="text-slate-400">→</span> {fmtDate(invoice.dueDate)}
        </Fact>
        <Fact label="Terms">{invoice.paymentTerms || '—'}</Fact>
        <Fact label="Billing address" wide>
          {invoice.billingAddress || '—'}
        </Fact>
        <Fact label="PO number">{invoice.poNumber || '—'}</Fact>
        <Fact label="Accounting export">
          {invoice.exportStatus}
          {invoice.exportReference && <span className="ml-1 text-slate-400">{invoice.exportReference}</span>}
        </Fact>
        {invoice.creditNoteOfNumber && (
          <Fact label="Credit note for" wide>
            <span className="font-medium text-rose-700">{invoice.creditNoteOfNumber}</span>
            {invoice.creditNoteOfIssueDate && (
              <span className="text-slate-400"> · {fmtDate(invoice.creditNoteOfIssueDate)}</span>
            )}
            {invoice.creditReason && <span className="block text-slate-600">{invoice.creditReason}</span>}
            {invoice.releasedCharges && (
              <span className="block text-[10px] text-slate-400">Charges released for re-invoicing</span>
            )}
          </Fact>
        )}
        {(invoice.creditNotes?.length ?? 0) > 0 && (
          <Fact label="Credit notes" wide>
            {invoice.creditNotes!.map((c) => (
              <span
                key={c.id}
                className={cn('mr-2 inline-block', c.status === 'Cancelled' && 'text-slate-400 line-through')}
                title={c.reason ?? undefined}
              >
                {c.number} <span className="tabular-nums">−{money(c.total, iso)}</span>
              </span>
            ))}
          </Fact>
        )}
        {invoice.status === 'Cancelled' && (
          <Fact label="Cancelled" wide>
            <span className="text-rose-700">
              {fmtDate(invoice.cancelledUtc)} — {invoice.cancellationReason || 'no reason given'}
            </span>
          </Fact>
        )}
      </div>

      {/* ── Lines ─────────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">Order</th>
              <th className="px-2 py-1.5 text-left font-semibold">Description</th>
              <th className="px-2 py-1.5 text-left font-semibold">Nominal</th>
              {showCategory && <th className="px-2 py-1.5 text-left font-semibold">Category</th>}
              <th className="px-2 py-1.5 text-left font-semibold">VAT</th>
              <th className="px-2 py-1.5 text-right font-semibold">Net</th>
              <th className="px-2 py-1.5 text-right font-semibold">VAT</th>
              <th className="px-2 py-1.5 text-right font-semibold">Total</th>
              <th className="w-6" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.map((l) => (
              <LineRow
                key={l.id}
                line={l}
                iso={iso}
                showCategory={showCategory}
                lineLabel={lineLabel}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* ── VAT bands, totals, hand changes, history ─────────────────── */}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-md border border-slate-200 bg-white p-3 text-xs">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">VAT</p>
          {bands.map((b) => (
            <div key={b.key} className="flex justify-between gap-3 py-0.5">
              <span className="text-slate-600">
                {b.label ? `${b.label} (${b.ratePct}%)` : `VAT ${b.ratePct}%`}
                <span className="text-slate-400"> on {money(b.net, iso)}</span>
              </span>
              <span className="tabular-nums text-slate-700">{money(b.vat, iso)}</span>
            </div>
          ))}
          <div className="mt-1.5 border-t border-slate-200 pt-1.5">
            <TotalRow label="Subtotal" value={money(invoice.subtotal, iso)} />
            <TotalRow label="VAT" value={money(invoice.vatTotal, iso)} />
            <TotalRow label="Total" value={money(invoice.total, iso)} strong />
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-3 text-xs">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Changed by hand
            </p>
            <InvoiceHistoryButton
              invoiceId={invoice.id}
              number={invoice.number}
              organisationId={organisationId}
            />
          </div>
          {overridden.length === 0 ? (
            <p className="italic text-slate-400">Nothing — every line went out as proposed.</p>
          ) : (
            <ul className="space-y-1.5">
              {overridden.map((l) => (
                <li key={l.id}>
                  <p className="font-medium text-slate-700">
                    {l.orderNumber ? `${l.orderNumber} · ` : ''}
                    {l.description}
                  </p>
                  {parseOverrides(l.overridesJson).map((o) => (
                    <p key={o.field} className="pl-2 text-slate-500">
                      {OVERRIDE_NAME[o.field] ?? o.field}:{' '}
                      <span className="text-slate-400 line-through">{o.from ?? '—'}</span>{' '}
                      → <span className="font-medium text-violet-700">{o.to ?? '—'}</span>
                    </p>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Shell>
  );
}

function LineRow({
  line: l,
  iso,
  showCategory,
  lineLabel,
}: {
  line: InvoiceLine;
  iso: string | null | undefined;
  showCategory: boolean;
  lineLabel: (key: string) => string;
}) {
  const overrides = parseOverrides(l.overridesJson);
  const changed = new Set(overrides.map((o) => o.field));
  const mark = (field: string) => (changed.has(field) ? 'font-semibold text-violet-700' : '');
  return (
    <tr className="align-top">
      <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-emerald-700">{l.orderNumber || '—'}</td>
      <td className="px-2 py-1.5 text-slate-800">
        {l.description}
        {l.products && <span className="block text-[10px] text-slate-500">{l.products}</span>}
      </td>
      <td className={cn('whitespace-nowrap px-2 py-1.5 font-mono text-[11px] text-slate-600', mark('Nominal'))}>
        {l.salesNominal || '—'}
      </td>
      {showCategory && (
        <td className={cn('whitespace-nowrap px-2 py-1.5 font-mono text-[11px] text-slate-600', mark('Category'))}>
          {l.accountingCategoryCode || '—'}
        </td>
      )}
      <td className={cn('px-2 py-1.5 text-slate-600', mark('Vat'))}>
        {l.taxRateName ? `${l.taxRateName} (${l.taxRatePct}%)` : `${l.taxRatePct}%`}
        {l.vatTreatmentKey && (
          <span
            className="ml-1 rounded-sm bg-slate-100 px-1 font-mono text-[10px] font-semibold text-slate-500"
            title="The VAT treatment line that set this line's default"
          >
            {lineLabel(l.vatTreatmentKey)}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-slate-700">{money(l.net, iso)}</td>
      <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-slate-500">
        {money(l.taxAmount, iso)}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-right font-semibold tabular-nums text-slate-800">
        {money(l.net + l.taxAmount, iso)}
      </td>
      <td className="px-1 py-1.5 text-center">
        {overrides.length > 0 && (
          <span
            title={overrides
              .map((o) => `${OVERRIDE_NAME[o.field] ?? o.field}: ${o.from ?? '—'} → ${o.to ?? '—'}`)
              .join('\n')}
          >
            <PencilLine className="inline h-3.5 w-3.5 text-violet-600" aria-label="Changed by hand" />
          </span>
        )}
      </td>
    </tr>
  );
}

/** Who did what to this invoice — its lines' creation and every "Overridden" entry. */
function InvoiceHistoryButton({
  invoiceId,
  number,
  organisationId,
}: {
  invoiceId: string;
  number: string;
  organisationId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const query = useAuditTrail(organisationId, 'Invoice', invoiceId, { enabled: open });
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Who changed what, and when"
        className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
      >
        <History className="h-3.5 w-3.5" />
        History
      </button>
      <HistoryDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`History · ${number}`}
        subtitle="who changed what, and when"
        entries={query.data ?? []}
        isLoading={query.isPending}
        error={query.isError ? 'Could not load this invoice history.' : null}
        hasMore={query.hasNextPage}
        isLoadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        entityLabel={(t) => (t === 'InvoiceLine' ? 'Line' : t === 'Invoice' ? 'Invoice' : t)}
        fieldLabel={(_t, field) => field}
        emptyMessage="Nothing recorded yet."
      />
    </>
  );
}

function Fact({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={cn('min-w-0', wide && 'col-span-2')}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <div className="break-words text-slate-700">{children}</div>
    </div>
  );
}

function TotalRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between py-0.5', strong ? 'font-semibold text-slate-800' : 'text-slate-600')}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function money(v: number, iso: string | null | undefined): string {
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: iso || 'EUR' }).format(v);
  } catch {
    return v.toFixed(2);
  }
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IE', { day: '2-digit', month: 'short', year: 'numeric' });
}
